import { useQuery } from '@tanstack/react-query';
import { Building2, Users, CreditCard, FileText } from 'lucide-react';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

interface DashboardStats {
  totalVenues: number;
  activeSubscriptions: number;
  totalBookings: number;
  revenue: {
    month: number;
    year: number;
  };
}

function StatCard({
  title,
  value,
  icon: Icon,
  subValue,
}: {
  title: string;
  value: string | number;
  icon: React.ElementType;
  subValue?: string;
}) {
  return (
    <div className="card p-6">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Icon className="h-8 w-8 text-primary-600" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          {subValue && (
            <p className="text-sm text-gray-500">{subValue}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await api.get('/admin/dashboard');
      return response.data;
    },
  });

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-48 mb-8"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card p-6 h-28 bg-gray-100"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-8">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Venues"
          value={stats?.totalVenues || 0}
          icon={Building2}
        />
        <StatCard
          title="Active Subscriptions"
          value={stats?.activeSubscriptions || 0}
          icon={CreditCard}
        />
        <StatCard
          title="Total Bookings"
          value={stats?.totalBookings || 0}
          icon={FileText}
        />
        <StatCard
          title="Monthly Revenue"
          value={formatCurrency(stats?.revenue.month || 0)}
          icon={Users}
          subValue={`${formatCurrency(stats?.revenue.year || 0)} this year`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Activity
          </h2>
          <p className="text-gray-500 text-sm">
            Activity feed will be displayed here once bookings are active.
          </p>
        </div>

        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Quick Actions
          </h2>
          <div className="space-y-3">
            <a
              href="/venues"
              className="block p-3 rounded-md border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <p className="font-medium text-gray-900">Add New Venue</p>
              <p className="text-sm text-gray-500">Register a new venue on the platform</p>
            </a>
            <a
              href="/users"
              className="block p-3 rounded-md border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <p className="font-medium text-gray-900">Manage Users</p>
              <p className="text-sm text-gray-500">View and manage system users</p>
            </a>
            <a
              href="/invoices"
              className="block p-3 rounded-md border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <p className="font-medium text-gray-900">View Invoices</p>
              <p className="text-sm text-gray-500">Check pending and paid invoices</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
